import type {
  ActivityDetail,
  ActivityVisibility,
  ParticipationMode,
} from './types'

export interface ActivityFormValues {
  title: string
  description: string
  categoryId: string
  startAt: string
  endAt: string
  city: string
  locationName: string
  meetingPoint: string
  maxParticipants: string
  participationMode: ParticipationMode
  visibility: ActivityVisibility
  repeatWeekly: boolean
  hasExpenses: boolean
  estimatedPrice: string
  priceDescription: string
}

export const EMPTY_FORM_VALUES: ActivityFormValues = {
  title: '',
  description: '',
  categoryId: '',
  startAt: '',
  endAt: '',
  city: '',
  locationName: '',
  meetingPoint: '',
  maxParticipants: '',
  participationMode: 'open',
  visibility: 'public',
  repeatWeekly: false,
  hasExpenses: false,
  estimatedPrice: '',
  priceDescription: '',
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

/**
 * Converts an API ISO timestamp to the local `datetime-local` value. The API
 * stores UTC; the form always shows the user's local wall-clock time.
 */
export function isoToLocalInput(value: string | null | undefined): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export function localInputToIso(value: string): string | null {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString()
}

export function nowLocalInput(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
    now.getDate(),
  )}T${pad(now.getHours())}:${pad(now.getMinutes())}`
}

export function addHoursToLocalInput(value: string, hours: number): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  date.setHours(date.getHours() + hours)
  return isoToLocalInput(date.toISOString())
}

export function activityToFormValues(
  activity: ActivityDetail,
  categoryId: string,
): ActivityFormValues {
  return {
    title: activity.title,
    description: activity.description,
    categoryId,
    startAt: isoToLocalInput(activity.startAt),
    endAt: isoToLocalInput(activity.endAt),
    city: activity.city ?? '',
    locationName: activity.locationName ?? '',
    meetingPoint: activity.meetingPoint ?? '',
    maxParticipants: String(activity.maxParticipants),
    participationMode: activity.participationMode,
    visibility: activity.visibility ?? 'public',
    repeatWeekly: activity.isRecurring === true,
    hasExpenses: activity.priceType === 'external_estimate',
    estimatedPrice:
      activity.estimatedPrice !== null ? String(activity.estimatedPrice) : '',
    priceDescription: activity.priceDescription ?? '',
  }
}
